import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { Button, Icon } from 'semantic-ui-react';
import { DateTime } from 'luxon';
import { AppState } from '../../types/app-state';
import { AuthState } from '../../types/auth-state';
import { post } from '../../utils/http';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { CurrentCSVSession } from '../../types/currentcsvsession';
import './PriceRevisionLogsPage.less'
import { Link } from 'react-router-dom';

let controller: AbortController;

const query = async (token:  string, setLogs: any) => {
  if (controller) {
    controller.abort();
  }
  controller = new AbortController();
  return post(`1.0/admin/prices-revisions`, {token}, undefined, {signal: controller.signal})
    .then((result) => {
      if (result.status === 1) {
        setLogs(result.data);
      }
    });
}

export const PriceRevisionLogsPage: React.FC = () => {
  const history = useHistory();
  const {token} = useSelector<AppState, Partial<AuthState>>(state => state.auth);
  const [current, setCurrent] = useLocalStorage<CurrentCSVSession | null>('price_revision_session', null);
  const [logs, setLogs] = useState<any[]>([]);
  const [busy, setBusy] = useState(true);
  const searchParams = new URLSearchParams(window.location.search);
  const highlight = searchParams.get('highlight');

  useEffect(() => {
    if (token && setLogs && setBusy) {
      query(token, setLogs);
      setBusy(false);
    }
  }, [token, setLogs, setBusy, current]);

  const handleRollback = async (id: string) => {
    if (token && !busy) {
      setBusy(true);
      await post(`1.0/admin/prices-revision/${id}/activate`, {token})
      if (id === current?.id) {
        setCurrent(null);
      }
      await query(token, setLogs);
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (token && !busy) {
      setBusy(true);

      if (current && current.id === id) {
        await post(`1.0/admin/prices-revision/${current.id}/delete`, {token});
        const stateNumber = current?.state ?? -1;
        if (stateNumber > -1) {
          let i = stateNumber;
          if (current?.id) {
            do {
              const currentSegment = window.localStorage.getItem(current.id + '_' + i);
              if (currentSegment) {
                window.localStorage.removeItem(current.id + '_' + i);
              }
              i++;
            } while(i < current.size);
          }
        }
        setCurrent(null);
      } else {
        await post(`1.0/admin/prices-revision/${id}/delete`, {token})
      }

      await query(token, setLogs);
      setBusy(false);
    }
  };

  const handleResumeUpload = () => {
    history.push('/admin/prices/new');
  };
  
  return (
    <div className="pricerevisionlogs-page">
      
      <div className="header">
        <h3>Price Revisions</h3>
        <Button primary as={Link} to={'prices/new'}>
          <Icon name='plus' />
          New Revision
        </Button>
      </div>
			
      {busy && <div className='loading-indicator'>Loading</div>}
      {(
				<table>
					<thead>
						<tr>
							<th>Price Revision Name</th>
							<th>Upload Date</th>
							<th>Status</th>
							<th></th>
						</tr>
					</thead>
					{logs && logs.length > 0 && (
						<tbody>
							{logs.map((log) => (
								<tr key={log.id} className={`${log.active ? 'currently-active' : ''} ${log.id === highlight ? 'highlight' : ''}`}>
									<td>{log.name}</td>
                  <td>{DateTime.fromISO(log.upload_date).toLocaleString(DateTime.DATETIME_FULL)}</td>
                  <td>
                    {log.id === current?.id && current && <>
                      {log.status !== 'ready' ? <>
                        Uploading {Math.ceil(current.state / current.size * 100)}%
                      </> : <>
                        Upload complete
                      </>}
                    </>}
                    {log.id !== current?.id && log.status}
                  </td>
                  <td>
                    {log.active && <b>Currently Active</b>}
                    {!log.active && log.status === 'ready' && <Button onClick={() => handleRollback(log.id)}>
                      {/* {log.id !== current?.id ? 'Rollback' : 'Activate'} */}
                      Activate
                    </Button>}
                    {!log.active && log.status !== 'ready' && <Button color='red' onClick={() => handleDelete(log.id)}>Delete</Button>}
                    {log.id === current?.id && log.status !== 'ready' && <Button color='teal' onClick={handleResumeUpload}>Resume Upload</Button>}
                  </td>
								</tr>
							))}
						</tbody>
					)}
					{!busy && logs && logs.length === 0 && (
						<tbody>
							<tr>
								<td colSpan={4} style={{ textAlign: 'center' }}>
									No Data
								</td>
							</tr>
						</tbody>
					)}
				</table>
			)}
    </div>
  );
};

export default PriceRevisionLogsPage;